// CommonProfileScreen.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shared screens: WelcomeScreen, ConsentScreen, IdentifyPatientScreen
//
// Each screen accepts an optional config prop that OVERRIDES specific fields.
// Anything not supplied falls back to the carenavigator.json defaults.

'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from '../../styles/carenavigator.module.css';
import BASE_CONFIG from '../../config/carenavigator.json';
import { BtnBack, BtnContinue } from '../Reusablecomponents/Button/Reusableutton';

import { isMobileVerified, storePatientSession } from '../../utils/storage/authStorage';
import { quickRegister, sendQuickOtp, verifyOtp } from '../../api/auth.api';

import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from 'react-router-dom';
import { HEIGHT_DEFAULT_IN, HEIGHT_MAX_IN, HEIGHT_MIN_IN, WEIGHT_DEFAULT_KG, WEIGHT_MAX_KG, WEIGHT_MIN_KG } from '../../config/constants';

const DEFAULT_IDENTIFY_STATE = {
  patientType: "new",

  mobile: "",
  mobileNumber:"",
  fullName: "",
  otp: "",

  otpSent: false,
  otpVerified: false,

  dob: "",

  ageGroup: "",
  marital: "",
  occupation: "",

  height: HEIGHT_DEFAULT_IN,
  weight: WEIGHT_DEFAULT_KG,

  profileSubStep: 0,

  errors: {},
};

/* ══════════════════════════════════════════════════════════════════
   MERGE HELPERS
══════════════════════════════════════════════════════════════════ */
function mergeWelcome(override = {}) {
    const W = BASE_CONFIG.screens.welcome;
    const A = BASE_CONFIG.app;
    return {
        title:     override.title     ?? W.title,
        subtitle:  override.subtitle  ?? W.subtitle,
        imageSrc:  override.imageSrc  ?? W.imageSrc,
        imageAlt:  override.imageAlt  ?? W.imageAlt,
        checklist: override.checklist ?? W.checklist,
        ctaLabel:  override.ctaLabel  ?? W.ctaLabel,
        logoSrc:   override.logoSrc   ?? A.logoSrc,
        logoAlt:   override.logoAlt   ?? A.logoAlt,
        homeUrl:   override.homeUrl   ?? A.homeUrl,
        backUrl:   override.backUrl   ?? '/lifeStages/',
        backLabel: override.backLabel ?? 'Back',
    };
}

function mergeConsent(override = {}) {
    const C = BASE_CONFIG.screens.consent;
    return {
        pageTitle:     override.pageTitle     ?? C.pageTitle,
        pageSubtitle:  override.pageSubtitle  ?? C.pageSubtitle,
        requiredItems: override.requiredItems ?? C.requiredItems,
        optionalItems: override.optionalItems ?? (C.optionalItems || []),
        sideCard: {
            title:      override.sideCard?.title      ?? C.sideCard.title,
            items:      override.sideCard?.items      ?? C.sideCard.items,
            infoBubble: override.sideCard?.infoBubble ?? C.sideCard.infoBubble,
            ctaLabel:   override.sideCard?.ctaLabel   ?? C.sideCard.ctaLabel,
        },
    };
}

function mergeIdentify(override = {}) {
    const IP = BASE_CONFIG.screens.identifyPatient;
    return {
        pageTitle:        override.pageTitle    ?? IP.pageTitle,
        pageSubtitle:     override.pageSubtitle ?? IP.pageSubtitle,
        tabs:             IP.tabs,
        existingBenefits: IP.existingBenefits,
    };
}

/* ══════════════════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════════════════ */
const ArrowRight = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
);
const ArrowLeft = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
);
const Check = ({ size = 12, color = 'white' }) => (
    <svg width={size} height={size} fill="none" stroke={color} strokeWidth={3} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);
const Lock = ({ size = 14, color = 'currentColor' }) => (
    <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
);
const Star = ({ size = 15, color = '#facc15' }) => (
    <svg width={size} height={size} fill={color} viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);
const ChevronDown = ({ size = 12 }) => (
    <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
);

/* ══════════════════════════════════════════════════════════════════
   SHARED PRIMITIVES
══════════════════════════════════════════════════════════════════ */
function Checkbox({ checked, onChange }) {
    return (
        <div
            className={`${styles.checkbox} ${checked ? styles.checked : ''}`}
            onClick={onChange}
            role="checkbox"
            aria-checked={checked}
        >
            {checked && <Check size={12} />}
        </div>
    );
}

function Toggle({ checked, onChange }) {
    return (
        <label className={styles.toggle}>
            <input type="checkbox" checked={checked} onChange={onChange} />
            <div className={styles.toggleTrack} />
            <div className={styles.toggleThumb} />
        </label>
    );
}

function SidePanel({ title, items, extra }) {
    return (
        <div className={styles.sidePanelCard}>
            {title && <div className={styles.sidePanelTitle}>{title}</div>}
            {items && (
                <div className={styles.sidePanelList}>
                    {items.map((item, i) => (
                        <div key={i} className={styles.sidePanelItem}>
                            <div className={styles.sidePanelItemIcon}>{item.icon}</div>
                            <div>
                                <div className={styles.sidePanelItemTitle}>{item.title}</div>
                                {item.desc && <div className={styles.sidePanelItemDesc}>{item.desc}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {extra}
        </div>
    );
}

function ChipGroup({ options, value, onChange, columns = 2 }) {
    return (
        <div className={styles.chipGrid} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {options.map(opt => (
                <button
                    key={opt}
                    className={`${styles.chipCard} ${value === opt ? styles.chipCardActive : ''}`}
                    onClick={() => onChange(opt)}
                    type="button"
                >
                    {value === opt && <span className={styles.chipCardCheck}><Check size={10} /></span>}
                    {opt}
                </button>
            ))}
        </div>
    );
}

function OtpInput({
  length = 6,
  value = "",
  onChange,
  error,
}) {
  const refs = useRef([]);

  // SAFE VALUE
  const safeValue = String(value || "");

  const digits = safeValue
    .split("")
    .slice(0, length);

  while (digits.length < length) {
    digits.push("");
  }

  const handleChange = (i, v) => {
    if (!/^\d?$/.test(v)) return;

    const arr = [...digits];

    arr[i] = v;

    const finalValue = arr.join("");

    onChange?.(finalValue);

    if (v && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handleKey = (i, e) => {
    if (
      e.key === "Backspace" &&
      !digits[i] &&
      i > 0
    ) {
      refs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);

    const arr = pasted.split("");

    while (arr.length < length) {
      arr.push("");
    }

    onChange?.(arr.join(""));

    const focusIndex = Math.min(
      pasted.length,
      length - 1
    );

    refs.current[focusIndex]?.focus();
  };

  return (
    <div>
      <div
        className={styles.otpRow}
        onPaste={handlePaste}
      >
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) =>
              (refs.current[i] = el)
            }
            className={`${styles.otpBox} ${
              digits[i]
                ? styles.filled
                : ""
            }`}
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digits[i] || ""}
            onChange={(e) =>
              handleChange(
                i,
                e.target.value
              )
            }
            onKeyDown={(e) =>
              handleKey(i, e)
            }
          />
        ))}
      </div>

      {error && (
        <div className={styles.errorText}>
          {error}
        </div>
      )}
    </div>
  );
}
export const toFtIn = (inches) => {
  const ft = Math.floor(inches / 12);
  const ins = inches % 12;
  return `${ft}.${ins} ft`;
};
function ResendCountdown({ onResend }) {
    const [sec, setSec] = useState(60);
    useEffect(() => {
        if (sec <= 0) return;
        const t = setTimeout(() => setSec(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [sec]);
    return (
        <div style={{ fontSize: 12, color: 'var(--fc-text-muted)', textAlign: 'center', marginTop: 8 }}>
            Didn't receive it?{' '}
            {sec > 0
                ? <span>Resend in <strong style={{ color: 'var(--fc-purple)' }}>{sec}s</strong></span>
                : (
                    <span
                        style={{ color: 'var(--fc-purple)', fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => { setSec(60); onResend?.(); }}
                    >
                        Resend OTP
                    </span>
                )
            }
        </div>
    );
}


export function IdentifyPatientScreena({
  onNext,
  onBack,
  setProfile,
  identifyState,
  setIdentifyState,
  savedState,
  config = {},
}) {
  const { pageTitle = "Create Your Profile", pageSubtitle = "" } = config;

  // Pull individual fields from shared state
  const {
  patientType,
  mobile,
  fullName,
  otp,
  otpSent,
  otpVerified,
  dob,
  ageGroup,
  marital,
  occupation,
  height,
  weight,
  profileSubStep,
  errors,
} = identifyState || DEFAULT_IDENTIFY_STATE;

  const { user } = useAuth();

  // ── Helpers to update shared state ────────────────────────────
  const updateIdentifyState = useCallback(
    (data) => setIdentifyState((prev) => ({ ...prev, ...data })),
    [setIdentifyState]
  );

  const updateErrors = useCallback(
    (data) => setIdentifyState((prev) => ({ ...prev, errors: data })),
    [setIdentifyState]
  );

  // Alias used in chip/slider onChange handlers (same name as CareNavigator)
  const updateIdentify = updateIdentifyState;

  // ── Prefill from auth user once ───────────────────────────────
  useEffect(() => {
    if (!user) return;
    updateIdentifyState({
      fullName: fullName || user?.fullName || "",
      mobile: mobile || user?.mobile || "",
      dob: dob || user?.dob || "",
      ageGroup: ageGroup || user?.ageGroup || "",
      marital: marital || user?.maritalStatus || "",
      occupation: occupation || user?.occupation || "",
      // height: height || user?.height || HEIGHT_DEFAULT_IN,
      height: height || (user?.height ? Math.round(user.height / 2.54) : HEIGHT_DEFAULT_IN),
      weight: weight || user?.weight || WEIGHT_DEFAULT_KG,
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ───────────────────────────────────────────────────
  const isVerified = isMobileVerified(mobile || user?.mobile || "");
  const inProfileFlow = profileSubStep > 0;
  const isNew = patientType === "new";

  // Tab config (mirrors CareNavigator's CONFIG.screens.identifyPatient)
  const ntab = {
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    sendOtpLabel: "Send OTP",
    continueLabel: "Continue",
    ageGroupOptions: ["10–14 years","15–19 years","20–25 years","26–35 years","36–45 years","46–55 years","55–64 years","65+ years"],
    maritalOptions: ["Single","Married","Partnered","Divorced","Widowed","Prefer not to say"],
    occupationOptions: ["Student","Working","Home maker"],
    heightMin: 100,
    heightMax: 220,
    weightMin: 30,
    weightMax: 200,
  };

  const etab = {
    sendOtpLabel: "Send OTP",
    securityNote: "Your records are protected and only shared with your care team.",
    redirectUrl: "/dashboard",
  };

  const existingBenefits = {
    title: "Existing Patient Benefits",
    items: [
      { icon: "📋", title: "Your records ready", desc: "Pick up right where you left off." },
      { icon: "⚡", title: "Faster check-in", desc: "Skip re-entering information you've already provided." },
      { icon: "🔒", title: "Secure access", desc: "OTP-verified, every time." },
    ],
  };

  // ── Live BMI ──────────────────────────────────────────────────
  const bmi = height > 0 ? +(weight / (height / 100) ** 2).toFixed(1) : 0;
  const bmiCategory =
    bmi < 18.5
      ? { label: "Underweight", color: "#3b82f6", bg: "#eff6ff" }
      : bmi < 25
      ? { label: "Normal weight", color: "#16a34a", bg: "#f0fdf4" }
      : bmi < 30
      ? { label: "Overweight", color: "#d97706", bg: "#fffbeb" }
      : { label: "Obese", color: "#dc2626", bg: "#fef2f2" };

  // ── First incomplete profile step ────────────────────────────
  const firstIncompleteStep = () => {
    if (!ageGroup) return 1;
    if (!marital) return 2;
    if (!occupation) return 3;
    return 4;
  };

  // ── OTP: Send ─────────────────────────────────────────────────
  const handleSendOtp = async () => {
    const errs = {};
    if (isNew && !fullName.trim()) errs.fullName = "Full name is required";
    if (!/^\d{10}$/.test(mobile)) errs.mobile = "Enter a valid 10-digit number";
    if (Object.keys(errs).length) { updateErrors(errs); return; }

    updateErrors({});

    // If mobile is already verified (returning user in same session), skip OTP
    if (isVerified) {
      updateIdentifyState({ otpVerified: true, profileSubStep: 1 });
      return;
    }

    try {
      const res = await sendQuickOtp({ fullName: fullName.trim(), mobileNumber: mobile.trim() });
      if (res.success) {
        updateIdentifyState({ otpSent: true });
      } else {
        updateErrors({ mobile: res.message || "Failed to send OTP" });
      }
    } catch (err) {
      updateErrors({ mobile: err.message || "Failed to send OTP" });
    }
  };

  // ── OTP: Verify ───────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (otp.length < 6) { updateErrors({ otp: "Enter all 6 digits" }); return; }
    updateErrors({});

    try {
      await verifyOtp({ fullName, mobileNumber: mobile, otp });

      const registerResponse = await quickRegister({
        fullName,
        mobileNumber: mobile,
        dob,
        otp,
        orgName: "Mukta Women's Clinic",
      });

      storePatientSession({
        ...registerResponse.data,
        fullName,
        mobile,
        dob,
        isVerified: true,
      });

      updateIdentifyState({
        otpVerified: true,
        otpSent: true,
        profileSubStep: firstIncompleteStep(),
      });
    } catch (err) {
      updateErrors({ otp: err.message || "Verification failed" });
    }
  };

  // ── Profile sub-step: Next ────────────────────────────────────
  const handleProfileNext = () => {
    if (profileSubStep === 1) {
      if (!ageGroup) { updateErrors({ ageGroup: "Please select your age group" }); return; }
      storePatientSession({ ageGroup });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 2 });
      return;
    }
    if (profileSubStep === 2) {
      if (!marital) { updateErrors({ marital: "Please select marital status" }); return; }
      storePatientSession({ maritalStatus: marital });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 3 });
      return;
    }
    if (profileSubStep === 3) {
      if (!occupation) { updateErrors({ occupation: "Please select occupation" }); return; }
      storePatientSession({ occupation });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 4 });
      return;
    }
    if (profileSubStep === 4) {
      storePatientSession({ height, weight });
      setProfile({ fullName, mobile, ageGroup, maritalStatus: marital, occupation, height, weight });
      onNext();
    }
  };

  // ── Profile sub-step: Back ────────────────────────────────────
  const handleProfileBack = () => {
    updateErrors({});
    if (profileSubStep === 1) {
      updateIdentifyState({ profileSubStep: 0, otpSent: isVerified ? false : true });
      return;
    }
    updateIdentifyState({ profileSubStep: profileSubStep - 1 });
  };

  // ── Side panel items ──────────────────────────────────────────
  const sideItems = isNew
    ? [
        { icon: "👤", title: "Create your profile", desc: "Get personalised care tailored to your needs." },
        { icon: "📁", title: "Save your records", desc: "Access reports and appointments anytime." },
        { icon: "🔒", title: "Secure & private", desc: "Your data is encrypted end-to-end." },
      ]
    : existingBenefits.items;

  // ── Side panel extra (BMI / info bubbles) ─────────────────────
  const profileSubStepExtra =
    profileSubStep === 4 ? (
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            background: bmiCategory.bg,
            border: `1.5px solid ${bmiCategory.color}`,
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: bmiCategory.color,
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Live BMI
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: bmiCategory.color, lineHeight: 1 }}>
              {bmi}
            </span>
            <span style={{ fontSize: 13, color: bmiCategory.color, fontWeight: 600 }}>
              {bmiCategory.label}
            </span>
          </div>
          <div
            style={{
              position: "relative",
              height: 8,
              borderRadius: 4,
              background:
                "linear-gradient(to right, #3b82f6 0%, #16a34a 30%, #d97706 60%, #dc2626 100%)",
              marginTop: 8,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
                left: `${Math.min(Math.max(((bmi - 15) / (40 - 15)) * 100, 0), 100)}%`,
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#fff",
                border: `2.5px solid ${bmiCategory.color}`,
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af" }}>
            <span>15</span>
            <span>18.5</span>
            <span>25</span>
            <span>30</span>
            <span>40</span>
          </div>
        </div>
      </div>
    ) : profileSubStep === 3 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>💼</span>
        <span className={styles.consentInfoBubbleText}>
          Your occupation helps us recommend care that fits your lifestyle and schedule.
        </span>
      </div>
    ) : profileSubStep === 2 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>💍</span>
        <span className={styles.consentInfoBubbleText}>
          Marital status helps us understand your life stage and personalise your care journey.
        </span>
      </div>
    ) : profileSubStep === 1 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>🎂</span>
        <span className={styles.consentInfoBubbleText}>
          Age helps us show you the most relevant health topics and care options.
        </span>
      </div>
    ) : null;

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.screenHeader}>
          <div className={styles.screenHeaderText}>
            <h1 className={`ava-heading-md ${styles.pageTitle}`}>{pageTitle}</h1>
            <p className="ava-text-body">{pageSubtitle}</p>
          </div>
          <div className={styles.screenHeaderIllustration}>
            {/* <div style={{ fontSize: 72, textAlign: "center" }}>
              👩‍💻
              </div> */}
              <img src='/images/profile.svg' className='h-20 w-20' />
          </div>
        </div>

        <div className={styles.twoCol}>
          <div>
            {/* ── PROFILE SUB-STEPS ── */}
            {inProfileFlow ? (
              <div className={styles.sectionBox}>
                <div className={`ava-text-xs ${styles.sectionBoxLabel}`}>
                  Complete your profile · Question {profileSubStep} of 4
                </div>

                {/* Step 1 — Age group */}
                {profileSubStep === 1 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      What's your age group?
                    </label>
                    <ChipGroup
                      options={ntab.ageGroupOptions}
                      value={ageGroup}
                      onChange={(v) => updateIdentify({ ageGroup: v })}
                      columns={3}
                    />
                    {errors?.ageGroup && (
                      <div className={styles.errorText}>{errors?.ageGroup}</div>
                    )}
                  </div>
                )}

                {/* Step 2 — Marital status */}
                {profileSubStep === 2 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      What's your marital status?
                    </label>
                    <ChipGroup
                      options={ntab.maritalOptions}
                      value={marital}
                      onChange={(v) => updateIdentify({ marital: v })}
                      columns={3}
                    />
                    {errors?.marital && (
                      <div className={styles.errorText}>{errors?.marital}</div>
                    )}
                  </div>
                )}

                {/* Step 3 — Occupation */}
                {profileSubStep === 3 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      Nice 👍 What best describes your occupation?
                    </label>
                    <ChipGroup
                      options={ntab.occupationOptions}
                      value={occupation}
                      onChange={(v) => updateIdentify({ occupation: v })}
                      columns={3}
                    />
                    {errors?.occupation && (
                      <div className={styles.errorText}>{errors?.occupation}</div>
                    )}
                  </div>
                )}

                {/* Step 4 — Height + Weight */}
                {profileSubStep === 4 && (
                  <>
                    <div className={styles.formGroup}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <label
                          className={`ava-text-body ${styles.label}`}
                          style={{ margin: 0 }}
                        >
                          What's your height?
                        </label>
                        <span className={styles.sliderValue}>
                          {/* {height} cm */}
                          {toFtIn(height)}
                          </span>
                      </div>
                      <input
                        className={styles.rangeSlider}
                        type="range"
                        min={toFtIn(HEIGHT_MIN_IN)}
                        max={toFtIn(HEIGHT_MAX_IN)}
                        step={1}
                        value={height}
                        onChange={(e) =>
                          updateIdentifyState({ height: Number(e.target.value) })
                        }
                      />
                      <div className={styles.rangeLabels}>
                        <span>{toFtIn(ntab.heightMin)} cm</span>
                        <span>{toFtIn(ntab.heightMax)} cm</span>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <label
                          className={`ava-text-body ${styles.label}`}
                          style={{ margin: 0 }}
                        >
                          What's your weight?
                        </label>
                        <span className={styles.sliderValue}>{weight} kg</span>
                      </div>
                      <input
                        className={styles.rangeSlider}
                        type="range"
                        min={ntab.weightMin}
                        max={ntab.weightMax}
                        step={1}
                        value={weight}
                        onChange={(e) =>
                          updateIdentifyState({ weight: Number(e.target.value) })
                        }
                      />
                      <div className={styles.rangeLabels}>
                        <span>{ntab.weightMin} kg</span>
                        <span>{ntab.weightMax} kg</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* ── OTP / MOBILE FORM (step 0) ── */
              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxLabel}>
                  Find or create your profile
                </div>

                {/* Name field — new patient, before OTP sent */}
                {isNew && !otpSent && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      {ntab.fullNameLabel}
                    </label>
                    <input
                      className={`${styles.input} ${errors.fullName ? styles.error : ""}`}
                      type="text"
                      placeholder={ntab.fullNamePlaceholder}
                      value={fullName}
                      onChange={(e) =>
                        updateIdentify({ fullName: e.target.value })
                      }
                    />
                    {errors?.fullName && (
                      <div className={styles.errorText}>{errors?.fullName}</div>
                    )}
                  </div>
                )}

                {/* Mobile + Send OTP */}
                {!otpSent && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={`ava-text-body ${styles.label}`}>
                        Mobile Number
                      </label>
                    {!isNew &&  <span className={styles.hint}>
                        We'll send a one-time password (OTP) to verify your number
                      </span>
                      }
                      <div
                        className={`${styles.phoneWrap} ${errors?.mobile ? styles.error : ""}`}
                        style={{ marginTop: 6 }}
                      >
                        <div className={styles.phoneCode}>🇮🇳 +91</div>
                        <input
                          className={styles.phoneInput}
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          maxLength={10}
                          value={mobile}
                          onChange={(e) =>
                            updateIdentify({
                              mobile: e.target.value.replace(/\D/g, ""),
                            })
                          }
                        />
                      </div>
                      {errors?.mobile && (
                        <div className={styles.errorText}>{errors?.mobile}</div>
                      )}
                    </div>

                    <button
                      className={`${styles.btnPrimary} ${styles.btnFull}`}
                      onClick={handleSendOtp}
                    >
                      {isVerified
                        ? ntab.continueLabel
                        : isNew
                        ? ntab.sendOtpLabel
                        : etab.sendOtpLabel}{" "}
                      <ArrowRight size={15} color="white" />
                    </button>

                    {!isNew && (
                      <div className={styles.securityBox} style={{ marginTop: 12 }}>
                        <span className={styles.securityBoxIcon}>🔒</span>
                        <span className={styles.securityBoxText}>
                          {etab.securityNote}
                        </span>
                      </div>
                    )}

                    <div className={styles.navRow} style={{ marginTop: 16 }}>
                      <button className={styles.btnOutline} onClick={onBack}>
                        <ArrowLeft size={15} /> Back
                      </button>
                    </div>
                  </>
                )}

                {/* OTP entry */}
                {otpSent && (
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--fc-text-muted)",
                        marginBottom: 12,
                        textAlign: "center",
                      }}
                    >
                      OTP sent to{" "}
                      <strong style={{ color: "var(--fc-purple)" }}>
                        +91 {mobile}
                      </strong>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--fc-purple)",
                          fontSize: 12,
                          cursor: "pointer",
                          marginLeft: 8,
                        }}
                        onClick={() =>
                          updateIdentifyState({ otpSent: false, otp: "" })
                        }
                      >
                        Change
                      </button>
                    </div>

                    <label
                      className={`ava-text-body ${styles.label}`}
                      style={{ textAlign: "center", display: "block", marginBottom: 8 }}
                    >
                      Enter OTP
                    </label>

                    <OtpInput
                      length={6}
                      value={otp}
                      onChange={(value) => updateIdentifyState({ otp: value })}
                      error={errors?.otp}
                    />

                    <ResendCountdown />

                    <div
                      style={{
                        marginTop: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        className={`${styles.btnPrimary} ${styles.btnFull}`}
                        onClick={handleVerifyOtp}
                      >
                        {isNew ? "Verify & Continue" : "Verify & Go to Dashboard"}{" "}
                        <ArrowRight size={15} color="white" />
                      </button>
                    </div>

                    {!isNew && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--fc-text-muted)",
                          textAlign: "center",
                          marginTop: 8,
                        }}
                      >
                        You'll be redirected to your patient dashboard after
                        verification.
                      </div>
                    )}

                    <div className={styles.navRow} style={{ marginTop: 16 }}>
                      <button
                        className={styles.btnOutline}
                        onClick={() =>
                          updateIdentifyState({ otpSent: false, otp: "" })
                        }
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className={styles.sideCol}>
            <SidePanel
              title={isNew ? "New Patient Benefits" : existingBenefits.title}
              items={sideItems}
              extra={profileSubStepExtra}
            />
          </div>

          {/* Profile sub-step nav */}
          {inProfileFlow && (
            <div className={styles.navRow} style={{ marginTop: 8 }}>
              <button className={styles.btnOutline} onClick={handleProfileBack}>
                <ArrowLeft size={15} /> Back
              </button>
              <button className={styles.btnPrimary} onClick={handleProfileNext}>
                {profileSubStep === 4 ? "Continue" : "Next"}{" "}
                <ArrowRight size={15} color="white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export function IdentifyPatientScreen({
  onNext,
  onBack,
  setProfile,
  identifyState, 
  setIdentifyState,
  savedState,
  config = {},
}) {
  const { pageTitle = "Create Your Profile", pageSubtitle = "" } = config;
  const navigate=useNavigate();
  const[showLogin,setShowLogin]=useState(false);
  // Pull individual fields from shared state
  const {
    patientType,
    mobile,
    fullName,
    otp,
    otpSent,
    otpVerified,
    dob,
    ageGroup,
    marital,
    occupation,
    height,
    weight,
    profileSubStep,
    errors,
  } = identifyState || DEFAULT_IDENTIFY_STATE;

  const { user } = useAuth();

  // ── Loading states ─────────────────────────────────────────────
  const [sendingOtp, setSendingOtp]     = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  // Refs for synchronous double-click guard (state update is async)
  const sendingRef   = useRef(false);
  const verifyingRef = useRef(false);
  const resendingRef = useRef(false);

  // ── Helpers to update shared state ────────────────────────────
  const updateIdentifyState = useCallback(
    (data) => setIdentifyState((prev) => ({ ...DEFAULT_IDENTIFY_STATE, ...(prev || {}), ...data })),
    [setIdentifyState]
  );

  const updateErrors = useCallback(
    (data) => setIdentifyState((prev) => ({ ...DEFAULT_IDENTIFY_STATE, ...(prev || {}), errors: data || {} })),
    [setIdentifyState]
  );

  // Alias used in chip/slider onChange handlers (same name as CareNavigator)
  const updateIdentify = updateIdentifyState;

  // ── Prefill from auth user once ───────────────────────────────
  useEffect(() => {
    if (!user) return;
      console.log("user.height raw:", user?.height);
  console.log("converted:", Math.round(user.height / 2.54));
    updateIdentifyState({
      fullName: fullName || user?.fullName || "",
      mobile: mobile || user?.mobile || "",
      dob: dob || user?.dob || "",
      ageGroup: ageGroup || user?.ageGroup || "",
      marital: marital || user?.maritalStatus || "",
      occupation: occupation || user?.occupation || "",
      height: height || user?.height || HEIGHT_DEFAULT_IN,
      // height: height || (user?.height ? Math.round(user.height / 2.54) : HEIGHT_DEFAULT_IN),
      weight: weight || user?.weight || WEIGHT_DEFAULT_KG,
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ───────────────────────────────────────────────────
  const isVerified = isMobileVerified(mobile || user?.mobile || "");
  const inProfileFlow = profileSubStep > 0;
  const isNew = patientType === "new";

  // Tab config (mirrors CareNavigator's CONFIG.screens.identifyPatient)
  const ntab = {
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    sendOtpLabel: "Send OTP",
    continueLabel: "Continue",
    ageGroupOptions: ["10–14 years","15–19 years","20–25 years","26–35 years","36–45 years","46–55 years","55–64 years","65+ years"],
    maritalOptions: ["Single","Married","Partnered","Divorced","Widowed","Prefer not to say"],
    occupationOptions: ["Student","Working","Home maker"],
    heightMin: 48,
    heightMax: 84,
    weightMin: 30,
    weightMax: 150,
  };

  const etab = {
    sendOtpLabel: "Send OTP",
    securityNote: "Your records are protected and only shared with your care team.",
    redirectUrl: "/dashboard",
  };

  const existingBenefits = {
    title: "Existing Patient Benefits",
    items: [
      { icon: "📋", title: "Your records ready", desc: "Pick up right where you left off." },
      { icon: "⚡", title: "Faster check-in", desc: "Skip re-entering information you've already provided." },
      { icon: "🔒", title: "Secure access", desc: "OTP-verified, every time." },
    ],
  };

  // ── Live BMI ──────────────────────────────────────────────────
  // const bmi = height > 0 ? +(weight / (height / 100) ** 2).toFixed(1) : 0;
  // const bmi = height > 0 ? +(weight / (height * 0.0254) ** 2).toFixed(1) : 0;
  // ✅ height stored in inches
const bmi = height > 0 ? +(weight / (height * 0.0254) ** 2).toFixed(1) : 0;
  const bmiCategory =
    bmi < 18.5
      ? { label: "Underweight", color: "#3b82f6", bg: "#eff6ff" }
      : bmi < 25
      ? { label: "Normal weight", color: "#16a34a", bg: "#f0fdf4" }
      : bmi < 30
      ? { label: "Overweight", color: "#d97706", bg: "#fffbeb" }
      : { label: "Obese", color: "#dc2626", bg: "#fef2f2" };

  // ── First incomplete profile step ────────────────────────────
  const firstIncompleteStep = () => {
    if (!ageGroup) return 1;
    if (!marital) return 2;
    if (!occupation) return 3;
    return 4;
  };

  // ── OTP: Send ─────────────────────────────────────────────────
  const handleSendOtp = async () => {
    // Double-click guard — ref is synchronous, state is async
    if (sendingOtp || sendingRef.current) return;

    const errs = {};
    if (isNew && !fullName.trim()) errs.fullName = "Full name is required";
    if (!/^\d{10}$/.test(mobile)) errs.mobile = "Enter a valid 10-digit number";
    if (Object.keys(errs).length) { updateErrors(errs); return; }

    updateErrors({});

    // If mobile is already verified (returning user in same session), skip OTP
    if (isVerified) {
      updateIdentifyState({ otpVerified: true, profileSubStep: 1 });
      return;
    }

    setSendingOtp(true);
    sendingRef.current = true;

    try {
      const res = await sendQuickOtp({ fullName: fullName.trim(), mobileNumber: mobile.trim() });
      if (res?.success) {
        updateIdentifyState({ otpSent: true, otp: "" }); // clear stale OTP digits
      } else {
        updateErrors({ mobile: res?.message || "Failed to send OTP" });
      }
    } catch (err) {
      if (err?.message?.toLowerCase().includes('already registered') ||
          err?.message?.toLowerCase().includes('please login')) {
          // updateIdentifyState({ showLogin: true });
          setShowLogin(true);
          updateErrors({ mobile: err.message });
        } else {
          updateErrors({ mobile: err.message || 'Failed to send OTP' });
        }
      // updateErrors({ mobile: err?.response?.data?.message || err?.message || "Failed to send OTP" });
    } finally {
      setSendingOtp(false);
      sendingRef.current = false;
    }
  };

  // ── OTP: Verify ───────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    // Double-click guard
    if (verifyingOtp || verifyingRef.current) return;

    const safeOtp = String(otp ?? "");
    if (safeOtp.length < 6) { updateErrors({ otp: "Enter all 6 digits" }); return; }
    updateErrors({});

    setVerifyingOtp(true);
    verifyingRef.current = true;

    try {
      await verifyOtp({ fullName, mobileNumber: mobile, otp: safeOtp });

      const registerResponse = await quickRegister({
        fullName,
        mobileNumber: mobile,
        dob,
        otp: safeOtp,
        orgName: "Mukta Women's Clinic",
      });

      storePatientSession({
        ...registerResponse?.data,
        fullName,
        mobile,
        dob,
        isVerified: true,
      });

      updateIdentifyState({
        otpVerified: true,
        otpSent: true,
        profileSubStep: firstIncompleteStep(),
      });
    } catch (err) {
      updateErrors({ otp: err?.response?.data?.message || err?.message || "Verification failed" });
    } finally {
      setVerifyingOtp(false);
      verifyingRef.current = false;
    }
  };

  // ── OTP: Resend ───────────────────────────────────────────────
  const handleResendOtp = async () => {
    // Double-click guard
    if (resendingOtp || resendingRef.current) return;

    updateErrors({});
    setResendingOtp(true);
    resendingRef.current = true;

    try {
      const res = await sendQuickOtp({ mobileNumber: mobile.trim() });
      if (res?.success) {
        updateIdentifyState({ otp: "" }); // clear boxes so user enters fresh code
      } else {
        updateErrors({ otp: res?.message || "Failed to resend OTP" });
      }
    } catch (err) {
      updateErrors({ otp: err?.response?.data?.message || err?.message || "Failed to resend OTP" });
    } finally {
      setResendingOtp(false);
      resendingRef.current = false;
    }
  };

  // ── Profile sub-step: Next ────────────────────────────────────
  const handleProfileNext = () => {
    if (profileSubStep === 1) {
      if (!ageGroup) { updateErrors({ ageGroup: "Please select your age group" }); return; }
      storePatientSession({ ageGroup });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 2 });
      return;
    }
    if (profileSubStep === 2) {
      if (!marital) { updateErrors({ marital: "Please select marital status" }); return; }
      storePatientSession({ maritalStatus: marital });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 3 });
      return;
    }
    if (profileSubStep === 3) {
      if (!occupation) { updateErrors({ occupation: "Please select occupation" }); return; }
      storePatientSession({ occupation });
      updateErrors({});
      updateIdentifyState({ profileSubStep: 4 });
      return;
    }
    if (profileSubStep === 4) {
      storePatientSession({ height, weight });
      setProfile({ fullName, mobile, ageGroup, maritalStatus: marital, occupation, height, weight });
      onNext();
    }
  };

  // ── Profile sub-step: Back ────────────────────────────────────
  const handleProfileBack = () => {
    updateErrors({});
    if (profileSubStep === 1) {
      updateIdentifyState({ profileSubStep: 0, otpSent: isVerified ? false : true });
      return;
    }
    updateIdentifyState({ profileSubStep: profileSubStep - 1 });
  };

  // ── Side panel items ──────────────────────────────────────────
  const sideItems = isNew
    ? [
        { icon: "👤", title: "Create your profile", desc: "Get personalised care tailored to your needs." },
        { icon: "📁", title: "Save your records", desc: "Access reports and appointments anytime." },
        { icon: "🔒", title: "Secure & private", desc: "Your data is encrypted end-to-end." },
      ]
    : existingBenefits.items;

  // ── Side panel extra (BMI / info bubbles) ─────────────────────
  const profileSubStepExtra =
    profileSubStep === 4 ? (
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            background: bmiCategory.bg,
            border: `1.5px solid ${bmiCategory.color}`,
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: bmiCategory.color,
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Live BMI
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: bmiCategory.color, lineHeight: 1 }}>
              {bmi}
            </span>
            <span style={{ fontSize: 13, color: bmiCategory.color, fontWeight: 600 }}>
              {bmiCategory.label}
            </span>
          </div>
          <div
            style={{
              position: "relative",
              height: 8,
              borderRadius: 4,
              background:
                "linear-gradient(to right, #3b82f6 0%, #16a34a 30%, #d97706 60%, #dc2626 100%)",
              marginTop: 8,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
                left: `${Math.min(Math.max(((bmi - 15) / (40 - 15)) * 100, 0), 100)}%`,
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#fff",
                border: `2.5px solid ${bmiCategory.color}`,
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af" }}>
            <span>15</span>
            <span>18.5</span>
            <span>25</span>
            <span>30</span>
            <span>40</span>
          </div>
        </div>
      </div>
    ) : profileSubStep === 3 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>💼</span>
        <span className={styles.consentInfoBubbleText}>
          Your occupation helps us recommend care that fits your lifestyle and schedule.
        </span>
      </div>
    ) : profileSubStep === 2 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>💍</span>
        <span className={styles.consentInfoBubbleText}>
          Marital status helps us understand your life stage and personalise your care journey.
        </span>
      </div>
    ) : profileSubStep === 1 ? (
      <div className={styles.consentInfoBubble} style={{ marginTop: 12 }}>
        <span>🎂</span>
        <span className={styles.consentInfoBubbleText}>
          Age helps us show you the most relevant health topics and care options.
        </span>
      </div>
    ) : null;

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.screenHeader}>
          <div className={styles.screenHeaderText}>
            <h1 className={`ava-heading-md ${styles.pageTitle}`}>{pageTitle}</h1>
            <p className="ava-text-body">{pageSubtitle}</p>
          </div>
          <div className={styles.screenHeaderIllustration}>
            <div style={{ fontSize: 72, textAlign: "center" }}>👩‍💻</div>
          </div>
        </div>

        <div className={styles.twoCol}>
          <div>
            {/* ── PROFILE SUB-STEPS ── */}
            {inProfileFlow ? (
              <div className={styles.sectionBox}>
                <div className={`ava-text-xs ${styles.sectionBoxLabel}`}>
                  Complete your profile · Question {profileSubStep} of 4
                </div>

                {/* Step 1 — Age group */}
                {profileSubStep === 1 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      What's your age group?
                    </label>
                    <ChipGroup
                      options={ntab.ageGroupOptions}
                      value={ageGroup}
                      onChange={(v) => updateIdentify({ ageGroup: v })}
                      columns={3}
                    />
                    {errors?.ageGroup && (
                      <div className={styles.errorText}>{errors?.ageGroup}</div>
                    )}
                  </div>
                )}

                {/* Step 2 — Marital status */}
                {profileSubStep === 2 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      What's your marital status?
                    </label>
                    <ChipGroup
                      options={ntab.maritalOptions}
                      value={marital}
                      onChange={(v) => updateIdentify({ marital: v })}
                      columns={3}
                    />
                    {errors?.marital && (
                      <div className={styles.errorText}>{errors?.marital}</div>
                    )}
                  </div>
                )}

                {/* Step 3 — Occupation */}
                {profileSubStep === 3 && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      Nice 👍 What best describes your occupation?
                    </label>
                    <ChipGroup
                      options={ntab.occupationOptions}
                      value={occupation}
                      onChange={(v) => updateIdentify({ occupation: v })}
                      columns={3}
                    />
                    {errors?.occupation && (
                      <div className={styles.errorText}>{errors?.occupation}</div>
                    )}
                  </div>
                )}

                {/* Step 4 — Height + Weight */}
                {profileSubStep === 4 && (
                  <>
                    <div className={styles.formGroup}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <label
                          className={`ava-text-body ${styles.label}`}
                          style={{ margin: 0 }}
                        >
                          What's your height?
                        </label>
                        <span className={styles.sliderValue}>{toFtIn(height)}</span>
                      </div>
                      <input
                        className={styles.rangeSlider}
                        type="range"
                        min={(HEIGHT_MIN_IN)}
                        max={(HEIGHT_MAX_IN)}
                        step={1} 
                        value={height} 
                        onChange={(e) =>
                          updateIdentifyState({ height: Number(e.target.value) })
                        }
                      />
                      <div className={styles.rangeLabels}>
                        <span>{toFtIn(HEIGHT_MIN_IN)} </span>
                        <span>{toFtIn(HEIGHT_MAX_IN)} </span>
                      </div>
                    </div>

                    <div className={styles.formGroup}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <label
                          className={`ava-text-body ${styles.label}`}
                          style={{ margin: 0 }}
                        >
                          What's your weight?
                        </label>
                        <span className={styles.sliderValue}>{weight} kg</span>
                      </div>
                      <input
                        className={styles.rangeSlider}
                        type="range"
                        min={WEIGHT_MIN_KG}
                        max={WEIGHT_MAX_KG}
                        step={1}
                        value={weight}
                        onChange={(e) =>
                          updateIdentifyState({ weight: Number(e.target.value) })
                        }
                      />
                      <div className={styles.rangeLabels}>
                        <span>{WEIGHT_MIN_KG} kg</span>
                        <span>{WEIGHT_MAX_KG} kg</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* ── OTP / MOBILE FORM (step 0) ── */
              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxLabel}>
                  Find or create your profile
                </div>

                {/* Name field — new patient, before OTP sent */}
                {isNew && !otpSent && (
                  <div className={styles.formGroup}>
                    <label className={`ava-text-body ${styles.label}`}>
                      {ntab.fullNameLabel}
                    </label>
                    <input
                      className={`${styles.input} ${errors?.fullName ? styles.error : ""}`}
                      type="text"
                      placeholder={ntab.fullNamePlaceholder}
                      value={fullName}
                      onChange={(e) =>
                        updateIdentify({ fullName: e.target.value })
                      }
                    />
                    {errors?.fullName && (
                      <div className={styles.errorText}>{errors?.fullName}</div>
                    )}
                  </div>
                )}

                {/* Mobile + Send OTP */}
                {!otpSent && (
                  <>
                    <div className={styles.formGroup}>
                      <label className={`ava-text-body ${styles.label}`}>
                        Mobile Number
                      </label>
                      {!isNew &&
                      <span className={styles.hint}>
                        We'll send a one-time password (OTP) to verify your number
                      </span>}
                      <div
                        className={`${styles.phoneWrap} ${errors?.mobile ? styles.error : ""}`}
                        style={{ marginTop: 6 }}
                      >
                        <div className={styles.phoneCode}>🇮🇳 +91</div>
                        <input
                          className={styles.phoneInput}
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          maxLength={10}
                          value={mobile}
                          onChange={(e) =>
                            updateIdentify({
                              mobile: e.target.value.replace(/\D/g, ""),
                            })
                          }
                        />
                      </div>
                      {/* asdf */}
                      {/* {errors?.mobile && (
                        <div className={styles.errorText}>{errors?.mobile}</div>
                      )} */}
                       {errors.mobile && <div className={`${styles.errorText} flex justify-between items-center`}
                    >{errors.mobile}

                      {showLogin && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            type="button"
                            className="ava-text-caption   hover:underline bg-transparent border-none p-0 cursor-pointer animate-bounce"
                            onClick={() => {
                              // updateIdentifyState({ patientType: 'existing', showLogin: false });
                              // updateErrors({});
                              navigate('/sign-in')
                            }}
                            style={{ color: "var(--primary-color)" }}
                          >
                            Login instead
                          </button>
                        </div>
                      )}
                      
                    </div>}

                    </div>

                    {/* <button
                      className={`${styles.btnPrimary} ${styles.btnFull} ${sendingOtp ? styles.btnDisabled : ""}`}
                      disabled={sendingOtp}
                      onClick={handleSendOtp}
                    >
                      {sendingOtp
                        ? "Sending OTP..."
                        : isVerified
                        ? ntab.continueLabel
                        : isNew
                        ? ntab.sendOtpLabel
                        : etab.sendOtpLabel}{" "}
                      {!sendingOtp && <ArrowRight size={15} color="white" />}
                    </button> */}

                    {!isNew && (
                      <div className={styles.securityBox} style={{ marginTop: 12 }}>
                        <span className={styles.securityBoxIcon}>🔒</span>
                        <span className={styles.securityBoxText}>
                          {etab.securityNote}
                        </span>
                      </div>
                    )}

                    <div className={styles.navRow} style={{ marginTop: 16 }}>
                      <button className={styles.btnOutline} onClick={onBack}>
                        <ArrowLeft size={15} /> Back
                      </button>
                        <button
                      className={`
                       btn-primary ${sendingOtp ? styles.btnDisabled : ""}`}
                      disabled={sendingOtp}
                      onClick={handleSendOtp}
                    >
                      {sendingOtp
                        ? "Sending OTP..."
                        : isVerified
                        ? ntab.continueLabel
                        : isNew
                        ? ntab.sendOtpLabel
                        : etab.sendOtpLabel}{" "}
                      {!sendingOtp && <ArrowRight size={15} color="white" />}
                    </button>

                    </div>
                  </>
                )}

                {/* OTP entry */}
                {otpSent && (
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--fc-text-muted)",
                        marginBottom: 12,
                        textAlign: "center",
                      }}
                    >
                      OTP sent to{" "}
                      <strong style={{ color: "var(--fc-purple)" }}>
                        +91 {mobile}
                      </strong>
                      <button
                        style={{
                          background: "none",
                          border: "none",
                          color: "var(--fc-purple)",
                          fontSize: 12,
                          cursor: "pointer",
                          marginLeft: 8,
                        }}
                        onClick={() =>
                          updateIdentifyState({ otpSent: false, otp: "" })
                        }
                      >
                        Change
                      </button>
                    </div>

                    <label
                      className={`ava-text-body ${styles.label}`}
                      style={{ textAlign: "center", display: "block", marginBottom: 8 }}
                    >
                      Enter OTP
                    </label>

                    <OtpInput
                      length={6}
                      value={otp ?? ""}
                      onChange={(value) => updateIdentifyState({ otp: value })}
                      error={errors?.otp}
                    />

                    <ResendCountdown
                      onResend={handleResendOtp}
                      disabled={resendingOtp}
                    />

                    <div
                      style={{
                        marginTop: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        className={`${styles.btnPrimary} ${styles.btnFull} ${verifyingOtp ? styles.btnDisabled : ""}`}
                        disabled={verifyingOtp}
                        onClick={handleVerifyOtp}
                      >
                        {verifyingOtp
                          ? "Verifying..."
                          : isNew
                          ? "Verify & Continue"
                          : "Verify & Go to Dashboard"}{" "}
                        {!verifyingOtp && <ArrowRight size={15} color="white" />}
                      </button>
                    </div>

                    {!isNew && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--fc-text-muted)",
                          textAlign: "center",
                          marginTop: 8,
                        }}
                      >
                        You'll be redirected to your patient dashboard after
                        verification.
                      </div>
                    )}

                    <div className={styles.navRow} style={{ marginTop: 16 }}>
                      <button
                        className={styles.btnOutline}
                        onClick={() =>
                          updateIdentifyState({ otpSent: false, otp: "" })
                        }
                      >
                        <ArrowLeft size={15} /> Back
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className={styles.sideCol}>
            <SidePanel
              title={isNew ? "New Patient Benefits" : existingBenefits.title}
              items={sideItems}
              extra={profileSubStepExtra}
            />
          </div>

          {/* Profile sub-step nav */}
          {inProfileFlow && (
            <div className={styles.navRow} style={{ marginTop: 8 }}>
              <button className={styles.btnOutline} onClick={handleProfileBack}>
                <ArrowLeft size={15} /> Back
              </button>
              <button className={styles.btnPrimary} onClick={handleProfileNext}>
                {profileSubStep === 4 ? "Continue" : "Next"}{" "}
                <ArrowRight size={15} color="white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
/* ══════════════════════════════════════════════════════════════════
   SCREEN 0 — WELCOME
══════════════════════════════════════════════════════════════════ */
export function WelcomeScreen({ onStart, config }) {
    const W = mergeWelcome(config);
    return (
        <div className={styles.page}>
            <div className={styles.card}>
                <div style={{ textAlign: 'center' }}>
                    <div className={styles.welcomeHeader}>
                        <a href={W.homeUrl} className={styles.welcomeLogoLink} rel="noopener noreferrer">
                            <img src={W.logoSrc} alt={W.logoAlt} style={{ height: 36, width: 'auto', display: 'block' }} />
                        </a>
                        <a href={W.backUrl} className={styles.welcomeBack}>{W.backLabel}</a>
                    </div>
                    <div className={styles.welcomeHero}>
                        <h2 className={`ava-heading-md  ${styles.welcomeTitle}`}>{W.title}</h2>
                        <p className={` ava-text-caption ${styles.welcomeSubtitle}`}>{W.subtitle}</p>
                        <img src={W.imageSrc} alt={W.imageAlt} className={styles.welcomeImage}
                            onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className={styles.welcomeChecklist}>
                        {W.checklist.map(text => (
                            <div key={text} className={styles.welcomeCheckItem}>
                                <div className={styles.welcomeCheckDot}><Star size={12} color="var(--primary-color)" /></div>
                                <span className={`ava-text-body`}>{text}</span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.welcomeCtaWrap}>
                        <button className={`btn-primary`} onClick={onStart}>
                            {W.ctaLabel} <ArrowRight size={16} color="white" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


